import API_URLS from '../config/apiConfig';
import api from '../interceptors/axiosConfig';
import type {
  ImageBlobData,
  PreloadStoreImageItem,
  StoreImageUploadResponse,
} from '../types/services/image';

export type { ImageBlobData, PreloadStoreImageItem, StoreImageUploadResponse } from '../types/services/image';

class ImageService {
  private imageCache = new Map<string, ImageBlobData>();
  private storeImageVersion = new Map<string, number>();

  private getVersionedUrl(storeId: string): string {
    const base = API_URLS.STORE_IMAGE_URL(storeId);
    const version = this.storeImageVersion.get(storeId) ?? 0;
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}v=${version}`;
  }

  // Fetch image as blob with authentication using axios
  async fetchImageAsBlob(imageUrl: string): Promise<string> {
    // Return default image immediately if no valid URL provided
    if (!imageUrl || imageUrl === null || imageUrl.trim() === '') {
      return '/default_store_logo.svg';
    }

    const cached = this.imageCache.get(imageUrl);
    if (cached && !cached.loading && !cached.error) {
      return cached.blobUrl;
    }

    this.imageCache.set(imageUrl, {
      blobUrl: '/default_store_logo.svg',
      loading: true,
      error: false,
    });

    try {
      const response = await api.get(imageUrl, {
        responseType: 'blob',
      });
      
      const blob = response.data;
      const blobUrl = URL.createObjectURL(blob);
      
      this.imageCache.set(imageUrl, {
        blobUrl,
        loading: false,
        error: false,
      });

      return blobUrl;
    } catch (error) {
      // Silently handle errors without console logging to avoid alerts
      this.imageCache.set(imageUrl, {
        blobUrl: '/default_store_logo.svg',
        loading: false,
        error: true,
      });

      return '/default_store_logo.svg';
    }
  }

  getCachedImage(imageUrl: string): ImageBlobData | null {
    return this.imageCache.get(imageUrl) || null;
  }

  // Get store image (uses versioned URL for cache-busting when needed)
  async getStoreImage(storeId: string): Promise<string> {
    const fullImageUrl = this.getVersionedUrl(storeId);
    return this.fetchImageAsBlob(fullImageUrl);
  }

  // Invalidate cached blob for a store's image and bump version
  invalidateStoreImage(storeId: string) {
    const url = this.getVersionedUrl(storeId);
    const cached = this.imageCache.get(url);
    if (cached) {
      this.cleanupBlobUrl(cached.blobUrl);
      this.imageCache.delete(url);
    }
    const current = this.storeImageVersion.get(storeId) ?? 0;
    this.storeImageVersion.set(storeId, current + 1);
  }

  // Force refresh and return new blob URL
  async refreshStoreImage(storeId: string): Promise<string> {
    this.invalidateStoreImage(storeId);
    return this.getStoreImage(storeId);
  }

  // Upload store image using axios
  async uploadStoreImage(storeId: string, file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post(API_URLS.UPLOAD_STORE_LOGO(storeId), formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    const result: StoreImageUploadResponse = response.data;
    return result.image_url || result.url || result.logo_url || '';
  }

  async preloadStoreImages(stores: PreloadStoreImageItem[]): Promise<Map<string, string>> {
    const imageMap = new Map<string, string>();
    const promises = stores.map(async (store) => {
      if (store.logo_image_url && store.logo_image_url !== null && store.logo_image_url.trim() !== '') {
        // If store has valid logo_image_url, fetch it using the store ID
        const imageUrl = API_URLS.STORE_IMAGE_URL(store.id);
        const blobUrl = await this.fetchImageAsBlob(imageUrl);
        imageMap.set(store.id, blobUrl);
      } else {
        imageMap.set(store.id, '/default_store_logo.svg');
      }
    });

    await Promise.all(promises);
    return imageMap;
  }

  cleanupBlobUrl(blobUrl: string) {
    if (blobUrl && blobUrl !== '/default_store_logo.svg' && blobUrl.startsWith('blob:')) {
      URL.revokeObjectURL(blobUrl);
    }
  }

  clearCache() {
    this.imageCache.forEach((data) => {
      this.cleanupBlobUrl(data.blobUrl);
    });
    this.imageCache.clear();
    this.storeImageVersion.clear();
  }
}

const imageService = new ImageService();
export default imageService;
